<template>
    <div>
        <!-- Togglers for Sales, Views, Discount -->
        <Toggler @toggleTogglar="(name, val) => toggleSortOption('sales', val)" :val="localFilters.sort_by == 'sales'" name="sales"
            :title="$t('search.selling_input')" />

        <Toggler @toggleTogglar="(name, val) => toggleSortOption('views', val)" :val="localFilters.sort_by == 'views'" name="views"
            :title="$t('search.viewed_input')" />
            
        <Toggler @toggleTogglar="(name, val) => toggleSortOption('discount', val)" :val="localFilters.sort_by == 'discount'" name="discount"
            :title="$t('search.discounts_input')" />

        <!-- Sort By Section -->
        <div>
            <div @click="toggleArrangeState" class="cursor-pointer flex items-center justify-between transition-height-transform  duration-200 delay-300 ">
                <div class="flex flex-col gap-2 flex-1">
                    <span class=" font-semibold">{{ $t('search.sort_by') }}</span>
                    <!-- Display selected sorts -->
                    <div v-if="selectedSorts.length > 0" class="flex flex-wrap gap-2 mt-2">
                        <div 
                            v-for="selectedSort in selectedSorts" 
                            :key="selectedSort.id"
                            class="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5 text-sm hover:bg-primary/15 transition-colors duration-200 group"
                        >
                            <!-- Sort name -->
                            <span class="text-primary font-medium text-xs">
                                {{ selectedSort.name }}
                            </span>
                            
                            <!-- Remove button -->
                            <button 
                                @click.stop="removeSort(selectedSort.id)"
                                class="ml-1 text-gray-400 hover:text-red-500 transition-colors duration-200 p-0.5 rounded-full hover:bg-red-50 group-hover:bg-red-50"
                                :title="$t('general.remove')"
                            >
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <span 
                    class="w-8 h-8 flex items-center justify-center cursor-pointer">
                    <img :class="` ${!arrangeState ? ' rotate-90 ' : ' rotate-0 '} transition-height-transform  cursor-pointer`"
                        src="../../assets/icons/down.svg" alt="">
                </span>
            </div>
            <div
                :class="`${!arrangeState ? 'min-h-[0px]' : 'min-h-[20px]'} transition-height-transform  text-[14px] text-[#484848BD]`">
                <div :class="` ${arrangeState ? 'flex' : 'hidden'}  flex-col gap-1 text-[14px] mt-3`">
                    <RadioBtn :title="$t('search.to_newer')"
                        :toShow="localFilters.sort_type == 'desc' && localFilters.sort_by == 'date'"
                        @fun="changeSortState('desc', 'date')" />
                    <RadioBtn :title="$t('search.to_older')"
                        :toShow="localFilters.sort_type == 'asc' && localFilters.sort_by == 'date'"
                        @fun="changeSortState('asc', 'date')" />
                    <RadioBtn :title="$t('search.from_highest_price')"
                        :toShow="localFilters.sort_type == 'desc' && localFilters.sort_by == 'price'"
                        @fun="changeSortState('desc', 'price')" />
                    <RadioBtn :title="$t('search.from_lowest_price')"
                        :toShow="localFilters.sort_type == 'asc' && localFilters.sort_by == 'price'"
                        @fun="changeSortState('asc', 'price')" />
                    <RadioBtn :title="$t('search.from_highest')"
                        :toShow="localFilters.sort_type == 'desc' && localFilters.sort_by == 'rating'"
                        @fun="changeSortState('desc', 'rating')" />
                    <RadioBtn :title="$t('search.from_lowest')"
                        :toShow="localFilters.sort_type == 'asc' && localFilters.sort_by == 'rating'"
                        @fun="changeSortState('asc', 'rating')" />
                </div>
            </div>
        </div>

        <!-- Product Display Section -->
        <div class="mt-4">
            <div @click="toggleDisplayProductState" class="  cursor-pointer flex items-center justify-between transition-height-transform  duration-200 delay-300 ">
                <div class="flex flex-col gap-2 flex-1">
                    <span class=" font-semibold">{{ $t('search.product_display') }}</span>
                    <!-- Display selected product types -->
                    <div v-if="selectedProductTypes.length > 0" class="flex flex-wrap gap-2 mt-2">
                        <div 
                            v-for="selectedProductType in selectedProductTypes" 
                            :key="selectedProductType.id"
                            class="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5 text-sm hover:bg-primary/15 transition-colors duration-200 group"
                        >
                            <!-- Product type name -->
                            <span class="text-primary font-medium text-xs">
                                {{ selectedProductType.name }}
                            </span>
                            
                            <!-- Remove button -->
                            <button 
                                @click.stop="removeProductType"
                                class="ml-1 text-gray-400 hover:text-red-500 transition-colors duration-200 p-0.5 rounded-full hover:bg-red-50 group-hover:bg-red-50"
                                :title="$t('general.remove')"
                            >
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <span 
                    class="w-8 h-8 flex items-center justify-center cursor-pointer">
                    <img :class="` ${!displayProductState ? ' rotate-90 ' : ' rotate-0 '} transition-height-transform  cursor-pointer`"
                        src="../../assets/icons/down.svg" alt="">
                </span>
            </div>
            <div
                :class="`${!displayProductState ? 'min-h-[0px]' : 'min-h-[20px]'} transition-height-transform  text-[14px] text-[#484848BD]`">
                <div
                    :class="`  ${displayProductState ? 'flex' : 'hidden'}  flex flex-col gap-1 text-[14px] mt-3`">
                    <RadioBtn :title="$t('search.product_all')" :toShow="localFilters.product_type == ''"
                        @fun="changeProductDisplay('')" />
                    <RadioBtn :title="$t('search.product_Retail')" :toShow="localFilters.product_type == 'retail'"
                        @fun="changeProductDisplay('retail')" />
                    <RadioBtn :title="$t('search.product_Wholesale')"
                        :toShow="localFilters.product_type == 'wholesale'"
                        @fun="changeProductDisplay('wholesale')" />
                    <RadioBtn :title="$t('search.product_offer')" :toShow="localFilters.product_type == 'offer'"
                        @fun="changeProductDisplay('offer')" />
                </div>
            </div>
        </div>

        <!-- Customer Reviews Section -->
        <div class="mt-4">
            <div  @click="toggleCustomerReviewState" class=" cursor-pointer flex items-center justify-between transition-height-transform  duration-200 delay-300 ">
                <div class="flex flex-col gap-2 flex-1">
                    <span class=" font-semibold">{{ $t('search.customer_reviews') }}</span>
                    <!-- Display selected ratings -->
                    <div v-if="selectedRatings.length > 0" class="flex flex-wrap gap-2 mt-2">
                        <div 
                            v-for="selectedRating in selectedRatings" 
                            :key="selectedRating.id"
                            class="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5 text-sm hover:bg-primary/15 transition-colors duration-200 group"
                        >
                            <!-- Rating name -->
                            <span class="text-primary font-medium text-xs">
                                {{ selectedRating.name }}
                            </span>
                            
                            <!-- Remove button -->
                            <button 
                                @click.stop="removeRating"
                                class="ml-1 text-gray-400 hover:text-red-500 transition-colors duration-200 p-0.5 rounded-full hover:bg-red-50 group-hover:bg-red-50"
                                :title="$t('general.remove')"
                            >
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <span
                    class="w-8 h-8 flex items-center justify-center cursor-pointer">
                    <img :class="` ${!customerReviewState ? ' rotate-90 ' : ' rotate-0 '} transition-height-transform  cursor-pointer`"
                        src="../../assets/icons/down.svg" alt="">
                </span>
            </div>
            <div
                :class="`${!customerReviewState ? 'min-h-[0px]' : 'min-h-[20px]'} transition-height-transform  text-[14px] text-[#484848BD]`">
                <div :class="` ${customerReviewState ? 'flex' : 'hidden'}  flex-col gap-1 text-[14px] mt-3`">
                    <RadioBtn :title="$t('search.all_ratings')" :toShow="localFilters.rating == ''"
                        @fun="changeClientRatingsState('')" />

                    <div @click="changeClientRatingsState('5')"
                        class="flex items-center gap-[7px] cursor-pointer">
                        <span :class="`flex items-center justify-center rounded-full border-[1.5px]  w-[25px] h-[25px] ${localFilters.rating == '5' ? 'border-primary' : 'border-[#E5E5E5]'
                            } `">
                            <span v-if="localFilters.rating == '5'"
                                :class="`block rounded-full  bg-primary  w-[11px] h-[11px] `"></span>
                        </span>
                        <div class=" flex gap-1">
                            <img v-for="(n) in 5" :key="n" src="../../assets/icons/star.svg" alt="" style="filter: brightness(0);">
                        </div>
                    </div>

                    <div @click="changeClientRatingsState('4')"
                        class="flex items-center gap-[7px] cursor-pointer">
                        <span :class="`flex items-center justify-center rounded-full border-[1.5px]  w-[25px] h-[25px] ${localFilters.rating == '4' ? 'border-primary' : 'border-[#E5E5E5]'
                            } `">
                            <span v-if="localFilters.rating == '4'"
                                :class="`block rounded-full  bg-primary  w-[11px] h-[11px] `"></span>
                        </span>
                        <div class=" flex gap-1">
                            <img v-for="(n) in 4" :key="n" src="../../assets/icons/star.svg" alt="" style="filter: brightness(0);">
                            <span>{{ $t('search.above') }}</span>
                        </div>
                    </div>

                    <div @click="changeClientRatingsState('3')"
                        class="flex items-center gap-[7px] cursor-pointer">
                        <span :class="`flex items-center justify-center rounded-full border-[1.5px]  w-[25px] h-[25px] ${localFilters.rating == '3' ? 'border-primary' : 'border-[#E5E5E5]'
                            } `">
                            <span v-if="localFilters.rating == '3'"
                                :class="`block rounded-full  bg-primary  w-[11px] h-[11px] `"></span>
                        </span>
                        <div class=" flex gap-1">
                            <img v-for="(n) in 3" :key="n" src="../../assets/icons/star.svg" alt="" style="filter: brightness(0);">
                            <span>{{ $t('search.above') }}</span>
                        </div>
                    </div>

                    <div @click="changeClientRatingsState('2')"
                        class="flex items-center gap-[7px] cursor-pointer">
                        <span :class="`flex items-center justify-center rounded-full border-[1.5px]  w-[25px] h-[25px] ${localFilters.rating == '2' ? 'border-primary' : 'border-[#E5E5E5]'
                            } `">
                            <span v-if="localFilters.rating == '2'"
                                :class="`block rounded-full  bg-primary  w-[11px] h-[11px] `"></span>
                        </span>
                        <div class=" flex gap-1">
                            <img v-for="(n) in 2" :key="n" src="../../assets/icons/star.svg" alt="" style="filter: brightness(0);">
                            <span>{{ $t('search.above') }}</span>
                        </div>
                    </div>

                    <div @click="changeClientRatingsState('1')"
                        class="flex items-center gap-[7px] cursor-pointer">
                        <span :class="`flex items-center justify-center rounded-full border-[1.5px]  w-[25px] h-[25px] ${localFilters.rating == '1' ? 'border-primary' : 'border-[#E5E5E5]'
                            } `">
                            <span v-if="localFilters.rating == '1'"
                                :class="`block rounded-full  bg-primary  w-[11px] h-[11px] `"></span>
                        </span>
                        <div class=" flex gap-1">
                            <img v-for="(n) in 1" :key="n" src="../../assets/icons/star.svg" alt="" style="filter: brightness(0);">
                            <span>{{ $t('search.above') }}</span>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Toggler from './Toggler.vue'
import RadioBtn from '~/components/generic/RadioBtn.vue'

export default {
    name: 'FilterSortDisplay',
    components: {
        Toggler,
        RadioBtn
    },
    props: {
        modelValue: {
            type: Object,
            required: true
        }
    },
    emits: ['update:modelValue', 'sort-changed', 'product-type-changed', 'rating-changed'],
    data() {
        return {
            arrangeState: false,
            displayProductState: false,
            customerReviewState: false,
            localFilters: { ...this.modelValue }
        }
    },
    computed: {
        selectedSorts() {
            const selectedSorts = []
            
            // Check for sort_by options
            if (this.localFilters.sort_by) {
                let sortName = ''
                
                // Handle boolean filters (views, sales, discount) that don't need sort_type
                if (this.localFilters.sort_by === 'sales' || this.localFilters.sort_by === 'views' || this.localFilters.sort_by === 'discount') {
                    switch (this.localFilters.sort_by) {
                        case 'sales':
                            sortName = this.$t('search.selling_input')
                            break
                        case 'views':
                            sortName = this.$t('search.viewed_input')
                            break
                        case 'discount':
                            sortName = this.$t('search.discounts_input')
                            break
                    }
                    
                    if (sortName) {
                        selectedSorts.push({
                            id: this.localFilters.sort_by,
                            name: sortName,
                            type: 'sort'
                        })
                    }
                }
                // Handle direction-based sorting that require sort_type
                else if (this.localFilters.sort_type) {
                    switch (this.localFilters.sort_by) {
                        case 'date':
                            sortName = this.localFilters.sort_type === 'desc' ? this.$t('search.to_newer') : this.$t('search.to_older')
                            break
                        case 'price':
                            sortName = this.localFilters.sort_type === 'desc' ? this.$t('search.from_highest_price') : this.$t('search.from_lowest_price')
                            break
                        case 'rating':
                            sortName = this.localFilters.sort_type === 'desc' ? this.$t('search.from_highest') : this.$t('search.from_lowest')
                            break
                    }
                    
                    if (sortName) {
                        selectedSorts.push({
                            id: `${this.localFilters.sort_by}_${this.localFilters.sort_type}`,
                            name: sortName,
                            type: 'sort'
                        })
                    }
                }
            }
            
            return selectedSorts
        },
        selectedProductTypes() {
            if (!this.localFilters.product_type) return []
            
            let productTypeName = ''
            switch (this.localFilters.product_type) {
                case 'retail':
                    productTypeName = this.$t('search.product_Retail')
                    break
                case 'wholesale':
                    productTypeName = this.$t('search.product_Wholesale')
                    break
                case 'offer':
                    productTypeName = this.$t('search.product_offer')
                    break
            }
            
            return productTypeName ? [{
                id: this.localFilters.product_type,
                name: productTypeName,
                type: 'product_type'
            }] : []
        },
        selectedRatings() {
            if (!this.localFilters.rating) return []
            
            const ratingText = this.localFilters.rating === '5' ? '5' : `${this.localFilters.rating}+`
            return [{
                id: this.localFilters.rating,
                name: `${ratingText} ${this.$t('search.stars')}`,
                type: 'rating'
            }]
        }
    },
    watch: {
        modelValue: {
            handler(newVal) {
                this.localFilters = { ...newVal }
            },
            deep: true
        },
        localFilters: {
            handler(newVal) {
                this.$emit('update:modelValue', newVal)
            },
            deep: true
        }
    },
    methods: {
        toggleArrangeState() {
            this.arrangeState = !this.arrangeState
        },
        toggleDisplayProductState() {
            this.displayProductState = !this.displayProductState
        },
        toggleCustomerReviewState() {
            this.customerReviewState = !this.customerReviewState
        },
        toggleSortOption(name, val) {
            if (val) {
                this.localFilters.sort_by = name
                if (name === 'views' || name === 'sales' || name === 'discount') {
                    // For views, sales, discount - don't set sort_type
                } else {
                    this.localFilters.sort_type = null
                }
            } else {
                this.localFilters.sort_by = ""
                this.localFilters.sort_type = null
            }
            this.$emit('sort-changed', { sort_by: this.localFilters.sort_by, sort_type: this.localFilters.sort_type })
        },
        changeSortState(state, by) {
            this.localFilters.sort_by = by
            this.localFilters.sort_type = state
            this.$emit('sort-changed', { sort_by: by, sort_type: state })
        },
        changeProductDisplay(state) {
            this.localFilters.product_type = state
            this.$emit('product-type-changed', state)
        },
        changeClientRatingsState(state) {
            this.localFilters.rating = state
            this.$emit('rating-changed', state)
        },
        removeSort(sortId) {
            const [sortBy, sortType] = sortId.split('_')
            if (sortBy === 'sales' || sortBy === 'views' || sortBy === 'discount') {
                this.localFilters.sort_by = ''
                this.localFilters.sort_type = null
            } else {
                this.localFilters.sort_by = ''
                this.localFilters.sort_type = null
            }
            // Emit event to trigger parent handler
            this.$emit('sort-changed', { sort_by: this.localFilters.sort_by, sort_type: this.localFilters.sort_type })
        },
        removeProductType() {
            this.localFilters.product_type = ''
            // Emit event to trigger parent handler
            this.$emit('product-type-changed', '')
        },
        removeRating() {
            this.localFilters.rating = ''
            // Emit event to trigger parent handler
            this.$emit('rating-changed', '')
        }
    }
}
</script>

<style scoped>
.transition-height-transform {
    transition-property: min-height, transform;
    transition-duration: 300ms;
    transition-timing-function: ease-in-out;
}
</style>

